import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { blogSchema, BlogFormData } from "../helpers/formValidation";
import { PageContainer } from "../components/PageContainer";
import { AlertMessage } from "../components/AlertMessage";
import { blogService } from "../services/blog.service";
import { mediaService } from "../services/media.service";
import { useApi } from "../api/useApi";
import { IDropdownOption } from "../interfaces/iDropdown.interface";
import { FormField } from "../components/form/FormField";
import { FormGrid } from "../components/form/FormGrid";
import { FormSelect } from "../components/form/FormSelect";
import { FormErrors } from "../components/form/FormErrors";
import {
  btnPrimary,
  btnSecondary,
  formInputClasses,
  formTextAreaClasses,
} from "../styles/formClasses";
import {
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  sectionCardClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { createJobPageClasses } from "../styles/pageSpecificClasses";
import { IBlogManage } from "../interfaces/blog/iBlogManage.interface";
import { IBlogImage } from "../interfaces/blog/iBlogImage.interface";
import { BlogPostStatusEnum } from "../enums/blogPostStatus.enum";

const defaultValues: BlogFormData = {
  id: 0,
  blogCategoryId: null,
  title: "",
  slug: "",
  summary: "",
  contentHtml: "",
  blogPostStatusId: BlogPostStatusEnum.Draft,
  publishedAt: "",
  readTimeMinutes: null,
  seoTitle: "",
  seoDescription: "",
  isFeatured: false,
  galleryImages: [],
};

export default function CreateBlogPost() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { request } = useApi();
  const [categories, setCategories] = useState<IDropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BlogFormData>({
    resolver: zodResolver(blogSchema),
    defaultValues,
  });

  const title = watch("title");
  const slug = watch("slug");
  const watchedGalleryImages = watch("galleryImages");
  const galleryImages = useMemo(() => watchedGalleryImages ?? [], [watchedGalleryImages]);
  const coverImage = useMemo(() => galleryImages.find((image) => image.isCover) ?? null, [galleryImages]);
  const galleryOnlyImages = useMemo(() => galleryImages.filter((image) => !image.isCover), [galleryImages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await blogService.getCategories();
        if (!cancelled) setCategories(data);
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (slug || !title) return;
    setValue("slug", slugify(title));
  }, [title, slug, setValue]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await blogService.getManage(Number(id), request);
        if (cancelled) return;
        reset({
          id: data.id,
          blogCategoryId: data.blogCategoryId ?? null,
          title: data.title,
          slug: data.slug,
          summary: data.summary ?? "",
          contentHtml: data.contentHtml,
          blogPostStatusId: data.blogPostStatusId,
          publishedAt: data.publishedAt ? data.publishedAt.slice(0, 10) : "",
          readTimeMinutes: data.readTimeMinutes ?? null,
          seoTitle: data.seoTitle ?? "",
          seoDescription: data.seoDescription ?? "",
          isFeatured: data.isFeatured,
          galleryImages: (data.images ?? []).map((image, index) => ({
            id: image.id,
            blogPostId: data.id,
            imageUrl: image.imageUrl,
            blobName: image.blobName ?? "",
            altText: image.altText ?? "",
            caption: image.caption ?? "",
            photographerName: image.photographerName ?? "",
            photographerUrl: image.photographerUrl ?? "",
            sourceName: image.sourceName ?? "Unsplash",
            sourceUrl: image.sourceUrl ?? "",
            locationName: image.locationName ?? "",
            sortOrder: image.sortOrder || index + 1,
            isCover: image.isCover,
          })),
        });
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, request, reset]);

  async function onSubmit(form: BlogFormData) {
    setSubmitError(null);

    const images = (form.galleryImages ?? [])
      .filter((image) => image.imageUrl.trim())
      .map((image, index) => ({
        id: image.id,
        blogPostId: form.id,
        imageUrl: image.imageUrl,
        blobName: normalizeOptionalString(image.blobName),
        altText: normalizeOptionalString(image.altText),
        caption: normalizeOptionalString(image.caption),
        photographerName: normalizeOptionalString(image.photographerName),
        photographerUrl: normalizeOptionalString(image.photographerUrl),
        sourceName: normalizeOptionalString(image.sourceName),
        sourceUrl: normalizeOptionalString(image.sourceUrl),
        locationName: normalizeOptionalString(image.locationName),
        sortOrder: index + 1,
        isCover: image.isCover,
      }));

    const model: IBlogManage = {
      id: form.id,
      blogCategoryId: form.blogCategoryId || null,
      title: form.title,
      slug: form.slug,
      summary: normalizeOptionalString(form.summary),
      contentHtml: form.contentHtml,
      blogPostStatusId: form.blogPostStatusId,
      publishedAt: form.publishedAt || null,
      readTimeMinutes: form.readTimeMinutes ?? null,
      seoTitle: normalizeOptionalString(form.seoTitle),
      seoDescription: normalizeOptionalString(form.seoDescription),
      isFeatured: form.isFeatured,
      images,
    };

    try {
      if (form.id) {
        await blogService.put(model, request);
      } else {
        await blogService.post(model, request);
      }

      navigate("/admin/blog");
    } catch (error: any) {
      setSubmitError(error?.message ?? "Failed to save blog post.");
    }
  }

  async function onCoverImageSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCover(true);
      setSubmitError(null);
      const uploaded = await mediaService.uploadImage(file, request);
      const nextImages = ensureSingleCover([
        createImage({
          imageUrl: uploaded.imageUrl,
          blobName: uploaded.blobName,
          blogPostId: watch("id"),
          isCover: true,
        }),
        ...galleryOnlyImages,
      ]);
      setValue("galleryImages", normalizeImageOrder(nextImages), { shouldDirty: true, shouldValidate: true });
    } catch (error: any) {
      setSubmitError(error?.message ?? "Failed to upload cover image.");
    } finally {
      event.target.value = "";
      setUploadingCover(false);
    }
  }

  async function onGalleryImagesSelected(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    try {
      setUploadingGallery(true);
      setSubmitError(null);
      const uploaded = await Promise.all(files.map((file) => mediaService.uploadImage(file, request)));
      const nextImages = [
        ...galleryImages,
        ...uploaded.map((item) =>
          createImage({
            imageUrl: item.imageUrl,
            blobName: item.blobName,
            blogPostId: watch("id"),
            isCover: false,
          })
        ),
      ];
      setValue("galleryImages", normalizeImageOrder(ensureSingleCover(nextImages)), { shouldDirty: true, shouldValidate: true });
    } catch (error: any) {
      setSubmitError(error?.message ?? "Failed to upload gallery image.");
    } finally {
      event.target.value = "";
      setUploadingGallery(false);
    }
  }

  function updateImage(index: number, patch: Partial<IBlogImage>) {
    const nextImages = galleryImages.map((image, currentIndex) => (
      currentIndex === index ? { ...image, ...patch } : image
    ));
    setValue("galleryImages", normalizeImageOrder(ensureSingleCover(nextImages)), { shouldDirty: true, shouldValidate: true });
  }

  function setAsCover(index: number) {
    updateImage(index, { isCover: true });
  }

  function removeImage(index: number) {
    const nextImages = galleryImages.filter((_, currentIndex) => currentIndex !== index);
    setValue("galleryImages", normalizeImageOrder(ensureSingleCover(nextImages)), { shouldDirty: true, shouldValidate: true });
  }

  if (loading) {
    return <div className={createJobPageClasses.loading}>Loading blog post...</div>;
  }

  return (
    <PageContainer maxWidth="5xl">
      <div className={centeredPageHeaderClasses}>
        <span className={subtleEyebrowClasses}>{id ? "Edit Post" : "New Post"}</span>
        <h1 className={`${createJobPageClasses.heading} ${pageHeadingClasses}`}>
          {id ? "Edit Blog Post" : "Create a New Blog Post"}
        </h1>
        <p className={pageLeadClasses}>
          Write beautifully presented editorial content for the public blog section.
        </p>
      </div>

      <div className={sectionCardClasses}>
        <form onSubmit={handleSubmit(onSubmit)} className={createJobPageClasses.form}>
          <FormErrors errors={errors} />

          <FormGrid>
            <FormField label="Title" error={errors.title?.message}>
              <input {...register("title")} className={formInputClasses} placeholder="e.g. Best Greek Islands for Summer Work" />
            </FormField>

            <FormField label="Slug" error={errors.slug?.message}>
              <input {...register("slug")} className={formInputClasses} placeholder="best-greek-islands-for-summer-work" />
            </FormField>
          </FormGrid>

          <FormGrid>
            <FormField label="Category" error="">
              <FormSelect<IDropdownOption, BlogFormData>
                name="blogCategoryId"
                control={control}
                options={categories}
                placeholder="Select a category"
                defaultZero={false}
              />
            </FormField>

            <FormField label="Status" error="">
              <select {...register("blogPostStatusId", { valueAsNumber: true })} className={formInputClasses}>
                <option value={BlogPostStatusEnum.Draft}>Draft</option>
                <option value={BlogPostStatusEnum.Published}>Published</option>
                <option value={BlogPostStatusEnum.Archived}>Archived</option>
              </select>
            </FormField>
          </FormGrid>

          <FormField label="Summary" error={errors.summary?.message}>
            <textarea {...register("summary")} className={formTextAreaClasses} placeholder="A short, card-friendly summary for the blog listing page." />
          </FormField>

          <FormField label="Content HTML" error={errors.contentHtml?.message}>
            <textarea {...register("contentHtml")} className={`${formTextAreaClasses} min-h-[20rem]`} placeholder="<p>Write the article body using HTML.</p>" />
          </FormField>

          <section className="space-y-4 rounded-[24px] border border-[#ddd1bf] bg-[#fffaf3] p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-[#213547]">Cover Image</h2>
              <label className={btnSecondary}>
                {uploadingCover ? "Uploading..." : coverImage ? "Replace Cover Image" : "Upload Cover Image"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={onCoverImageSelected}
                  disabled={uploadingCover}
                />
              </label>
            </div>

            {!coverImage ? (
              <p className="text-sm text-[#5c5b57]">No cover image yet. Upload one to highlight the post.</p>
            ) : (
              <ImageEditorCard
                image={coverImage}
                imageIndex={galleryImages.findIndex((image) => image.isCover)}
                errors={errors.galleryImages?.[galleryImages.findIndex((image) => image.isCover)]}
                onChange={updateImage}
                onRemove={removeImage}
                showCoverActions={false}
              />
            )}
          </section>

          <FormGrid>
            <FormField label="Published Date" error="">
              <input type="date" {...register("publishedAt")} className={formInputClasses} />
            </FormField>

            <FormField label="Read Time (minutes)" error="">
              <input
                type="number"
                {...register("readTimeMinutes", {
                  setValueAs: (value) => (value === "" ? null : Number(value)),
                })}
                className={formInputClasses}
                placeholder="Optional"
              />
            </FormField>
          </FormGrid>

          <FormGrid>
            <FormField label="SEO Title" error={errors.seoTitle?.message}>
              <input {...register("seoTitle")} className={formInputClasses} placeholder="Optional SEO title" />
            </FormField>

            <FormField label="SEO Description" error={errors.seoDescription?.message}>
              <input {...register("seoDescription")} className={formInputClasses} placeholder="Optional SEO description" />
            </FormField>
          </FormGrid>

          <section className="space-y-4 rounded-[24px] border border-[#ddd1bf] bg-[#fffaf3] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[#213547]">Gallery Images</h2>
                <p className="text-sm text-[#5c5b57]">Upload files to Azure and save the credit details with each image.</p>
              </div>
              <label className={btnSecondary}>
                {uploadingGallery ? "Uploading..." : "Upload Gallery Images"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={onGalleryImagesSelected}
                  disabled={uploadingGallery}
                />
              </label>
            </div>

            {galleryOnlyImages.length === 0 ? (
              <p className="text-sm text-[#5c5b57]">No gallery images yet.</p>
            ) : (
              <div className="space-y-4">
                {galleryOnlyImages.map((image) => {
                  const index = galleryImages.findIndex((item) => item.imageUrl === image.imageUrl && item.blobName === image.blobName && !item.isCover);
                  return (
                    <ImageEditorCard
                      key={`${image.blobName || image.imageUrl}-${index}`}
                      image={image}
                      imageIndex={index}
                      errors={errors.galleryImages?.[index]}
                      onChange={updateImage}
                      onRemove={removeImage}
                      onSetAsCover={setAsCover}
                      showCoverActions
                    />
                  );
                })}
              </div>
            )}
          </section>

          <label className="flex items-center gap-3 text-sm text-[#34566a]">
            <input type="checkbox" {...register("isFeatured")} className="h-4 w-4 rounded border-slate-300" />
            Featured post
          </label>

          <AlertMessage type="error" message={submitError} />

          <div className={createJobPageClasses.actions}>
            <button type="button" onClick={() => navigate(-1)} className={btnSecondary}>
              Cancel
            </button>
            <button type="submit" className={btnPrimary}>
              {id ? "Update Post" : "Create Post"}
            </button>
          </div>
        </form>
      </div>
    </PageContainer>
  );
}

type ImageEditorCardProps = {
  image: IBlogImage;
  imageIndex: number;
  errors: any;
  onChange: (index: number, patch: Partial<IBlogImage>) => void;
  onRemove: (index: number) => void;
  onSetAsCover?: (index: number) => void;
  showCoverActions: boolean;
};

function ImageEditorCard({ image, imageIndex, errors, onChange, onRemove, onSetAsCover, showCoverActions }: ImageEditorCardProps) {
  return (
    <article className="space-y-4 rounded-[20px] border border-[#d9ccb8] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[#34566a]">{image.isCover ? "Cover Image" : "Gallery Image"}</span>
        <div className="flex flex-wrap gap-2">
          {showCoverActions && onSetAsCover && (
            <button type="button" className={btnSecondary} onClick={() => onSetAsCover(imageIndex)}>
              Set as Cover
            </button>
          )}
          <button type="button" className={btnSecondary} onClick={() => onRemove(imageIndex)}>
            Remove
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-[18px] border border-[#ddd1bf] bg-[#fffaf3]">
        <img src={image.imageUrl} alt={image.altText || ""} className="h-48 w-full object-cover" />
      </div>

      <FormGrid>
        <FormField label="Image URL" error={errors?.imageUrl?.message}>
          <input value={image.imageUrl} onChange={(e) => onChange(imageIndex, { imageUrl: e.target.value })} className={formInputClasses} />
        </FormField>

        <FormField label="Blob Name" error="">
          <input value={image.blobName ?? ""} onChange={(e) => onChange(imageIndex, { blobName: e.target.value })} className={formInputClasses} />
        </FormField>
      </FormGrid>

      <FormGrid>
        <FormField label="Alt Text" error={errors?.altText?.message}>
          <input value={image.altText ?? ""} onChange={(e) => onChange(imageIndex, { altText: e.target.value })} className={formInputClasses} placeholder="Describe the image" />
        </FormField>

        <FormField label="Caption" error={errors?.caption?.message}>
          <input value={image.caption ?? ""} onChange={(e) => onChange(imageIndex, { caption: e.target.value })} className={formInputClasses} placeholder="Optional caption" />
        </FormField>
      </FormGrid>

      <FormGrid>
        <FormField label="Photographer" error={errors?.photographerName?.message}>
          <input value={image.photographerName ?? ""} onChange={(e) => onChange(imageIndex, { photographerName: e.target.value })} className={formInputClasses} placeholder="Photographer name" />
        </FormField>

        <FormField label="Photographer Link" error={errors?.photographerUrl?.message}>
          <input value={image.photographerUrl ?? ""} onChange={(e) => onChange(imageIndex, { photographerUrl: e.target.value })} className={formInputClasses} placeholder="https://unsplash.com/@..." />
        </FormField>
      </FormGrid>

      <FormGrid>
        <FormField label="Source" error={errors?.sourceName?.message}>
          <input value={image.sourceName ?? ""} onChange={(e) => onChange(imageIndex, { sourceName: e.target.value })} className={formInputClasses} placeholder="e.g. Unsplash" />
        </FormField>

        <FormField label="Source Link" error={errors?.sourceUrl?.message}>
          <input value={image.sourceUrl ?? ""} onChange={(e) => onChange(imageIndex, { sourceUrl: e.target.value })} className={formInputClasses} placeholder="https://unsplash.com/photos/..." />
        </FormField>
      </FormGrid>

      <FormField label="Photo Location" error={errors?.locationName?.message}>
        <input value={image.locationName ?? ""} onChange={(e) => onChange(imageIndex, { locationName: e.target.value })} className={formInputClasses} placeholder="e.g. Chania, Crete, Greece" />
      </FormField>
    </article>
  );
}

function createImage({
  imageUrl,
  blobName,
  blogPostId,
  isCover,
}: {
  imageUrl: string;
  blobName: string;
  blogPostId: number;
  isCover: boolean;
}): IBlogImage {
  return {
    id: 0,
    blogPostId,
    imageUrl,
    blobName,
    altText: "",
    caption: "",
    photographerName: "",
    photographerUrl: "",
    sourceName: "Unsplash",
    sourceUrl: "",
    locationName: "",
    sortOrder: 0,
    isCover,
  };
}

function ensureSingleCover(images: IBlogImage[]) {
  let coverAssigned = false;
  const normalized = images.map((image) => {
    if (image.isCover && !coverAssigned) {
      coverAssigned = true;
      return image;
    }

    return { ...image, isCover: false };
  });

  if (!coverAssigned && normalized.length > 0) {
    normalized[0] = { ...normalized[0], isCover: true };
  }

  return normalized;
}

function normalizeImageOrder(images: IBlogImage[]) {
  return images.map((image, index) => ({
    ...image,
    sortOrder: index + 1,
  }));
}

function normalizeOptionalString(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
